/* JFCRX-7754. The preview is one ten-row document per pager page. When those
 * ten rows do not fit on one sheet, the document continues on a second sheet
 * below the visible part of the viewer, and the only sign of it was the
 * "(1 / 2)" footer suffix, itself below the fold. This notice sits above the
 * preview and says so, with the sheet count. All three report viewers use it
 * (Production and the builder reports, Tray Check Summary / OptiSort, and
 * Substitution).
 *
 * The count comes from the document's footer `render` callback, which
 * react-pdf calls with the real totalPages once the document is laid out
 * (onSheetCount on the documents). */
import { useCallback, useState } from "react";
import styled from "styled-components";

const Notice = styled.div`
  margin: 8px 0;
  padding: 6px 12px;
  border-left: 4px solid #089bab;
  background-color: #e6f5f7;
  color: #454b60;
  font-family: "Poppins";
  font-size: 14px;
  line-height: 20px;
`;

const PDFPreviewSheetNotice = ({ t, sheets }) =>
  sheets > 1 ? (
    <Notice role="status">
      {t("previewContinuesNotice", { count: sheets })}
    </Notice>
  ) : null;

export default PDFPreviewSheetNotice;

/**
 * The sheet count of the preview on screen. Each time the viewer builds a
 * preview document (inside its useMemo), it calls `newSheetCounter()` and
 * gives the document the returned `onSheetCount`. A count is shown only while
 * the counter it came from belongs to the preview on screen
 * (`sheetsFor(counter)`), so a count from the previous pager page, font or
 * width mode is never shown against the next one while that one renders.
 * Keying, rather than resetting in an effect, costs no extra PDF layout.
 * react-pdf reports the count once per sheet; an unchanged count bails out
 * of the state update, so the repeats do not re-render the viewer. (The PDF
 * itself is not laid out again either way: the viewers memoize the preview
 * document.)
 */
export const usePreviewSheetCount = () => {
  const [counted, setCounted] = useState({ counter: null, sheets: 1 });

  const newSheetCounter = useCallback(() => {
    const counter = {};
    counter.onSheetCount = (sheets) =>
      setCounted((prev) =>
        prev.counter === counter && prev.sheets === sheets
          ? prev
          : { counter, sheets },
      );
    return counter;
  }, []);

  const sheetsFor = (counter) =>
    counted.counter === counter ? counted.sheets : 1;

  return { newSheetCounter, sheetsFor };
};
